import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, NavLink, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import io from 'socket.io-client';
import { useBranding } from '@/contexts/BrandingContext';
import { useLanguage } from '@/i18n/LanguageContext';

// Components
import AdminDashboardHome from '@/components/admin/AdminDashboardHome';
import StaffPage from '@/components/admin/StaffPage';
import TeamsPage from '@/components/admin/TeamsPage';
import AttendancePage from '@/components/admin/AttendancePage';
import AdminReportsPage from '@/components/admin/AdminReportsPage';
import PayrollReportsPage from '@/components/admin/PayrollReportsPage';
import ActivityReportsPage from '@/components/admin/ActivityReportsPage';
// New modular report pages
import ReportsHubPage from '@/components/admin/reports/ReportsHubPage';
import DailyReportPage from '@/components/admin/reports/DailyReportPage';
import MonthlyReportPage from '@/components/admin/reports/MonthlyReportPage';
import ScreenshotsReportPage from '@/components/admin/reports/ScreenshotsReportPage';
import GPSTrackingReportPage from '@/components/admin/reports/GPSTrackingReportPage';
import StaffActivitiesPage from '@/components/admin/reports/StaffActivitiesPage';
import UsageReportPage from '@/components/admin/reports/UsageReportPage';
import UsageTimelinePage from '@/components/admin/reports/UsageTimelinePage';
import AppsSummaryPage from '@/components/admin/reports/AppsSummaryPage';
import PayrollTimesheetPage from '@/components/admin/reports/PayrollTimesheetPage';
import PayrollSalaryPage from '@/components/admin/reports/PayrollSalaryPage';
import PayrollOvertimePage from '@/components/admin/reports/PayrollOvertimePage';
import PayrollLeavePage from '@/components/admin/reports/PayrollLeavePage';
import PayrollAuditPage from '@/components/admin/reports/PayrollAuditPage';
import AdminSettingsPage from '@/components/admin/AdminSettingsPage';
import PaymentsPage from '@/components/admin/PaymentsPage';
import ActivitiesPage from '@/components/admin/ActivitiesPage';
import StaffScreenshotsPage from '@/components/admin/StaffScreenshotsPage';
import StaffGPSTrackingPage from '@/components/admin/StaffGPSTrackingPage';
import StaffGPSSettingsPage from '@/components/admin/StaffGPSSettingsPage';
import AdminStaffSessionsPage from '@/components/admin/AdminStaffSessionsPage';
import StaffAttendanceDetailPage from '@/components/admin/StaffAttendanceDetailPage';
import UsagePolicyPage from '@/components/admin/UsagePolicyPage';
import UsageDashboardPage from '@/components/admin/UsageDashboardPage';
import StaffUsageDetailPage from '@/components/admin/StaffUsageDetailPage';
import AppUrlDetailsPage from '@/components/admin/AppUrlDetailsPage';
import AlertsNotification from '@/components/admin/AlertsNotification';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import AdminLeaveManagementPage from '@/components/admin/AdminLeaveManagementPage';
import AdminNotificationsPage from '@/components/admin/AdminNotificationsPage';
import NotificationRecipientsPage from '@/components/admin/NotificationRecipientsPage';
import LanguageSelector from '@/components/LanguageSelector';
import ThemeToggle from '@/components/ThemeToggle';

// UI Components
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  LayoutDashboard, 
  Users, 
  UserCog,
  UsersRound,
  ClipboardCheck, 
  FileText, 
  FileSpreadsheet,
  LogOut, 
  Menu,
  X,
  ArrowLeft,
  Settings,
  Building2,
  CreditCard,
  Activity,
  Shield,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  BarChart3,
  CalendarOff,
  Bell
} from 'lucide-react';

function CompanyAdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { branding } = useBranding();
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [socket, setSocket] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isBackdoor, setIsBackdoor] = useState(false);
  const [backdoorToken, setBackdoorToken] = useState(null);
  const [pendingLeaveCount, setPendingLeaveCount] = useState(0);
  
  // Check if current path is under reports submenu
  const isReportsSubPath = [
    '/admin/activities',
    '/admin/usage-policy',
    '/admin/reports',
    '/admin/payroll-reports',
    '/admin/activity-reports'
  ].some(path => location.pathname.startsWith(path));
  
  // Reports menu open state - default to open if on a reports subpage
  const [reportsMenuOpen, setReportsMenuOpen] = useState(false); // unused, kept for backwards compat
  
  useEffect(() => {
    // Check for backdoor token in URL
    const urlToken = searchParams.get('backdoor_token');
    if (urlToken) {
      setBackdoorToken(urlToken);
      setIsBackdoor(true);
      // Store in sessionStorage for this tab only
      sessionStorage.setItem('backdoor_token', urlToken);
      // Remove token from URL for cleaner look
      searchParams.delete('backdoor_token');
      setSearchParams(searchParams, { replace: true });
    } else {
      // Check sessionStorage for existing backdoor session
      const storedToken = sessionStorage.getItem('backdoor_token');
      if (storedToken) {
        setBackdoorToken(storedToken);
        setIsBackdoor(true);
      }
    }
  }, [searchParams, setSearchParams]);
  
  // Add axios interceptor for backdoor token
  useEffect(() => {
    const interceptorId = axios.interceptors.request.use((config) => {
      const storedToken = sessionStorage.getItem('backdoor_token');
      if (storedToken && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${storedToken}`;
      }
      return config;
    });
    
    return () => {
      axios.interceptors.request.eject(interceptorId);
    };
  }, []);
  
  useEffect(() => {
    // Get user info
    const fetchUserData = async () => {
      try {
        // Use backdoor token if available, otherwise use cookies
        const storedToken = sessionStorage.getItem('backdoor_token');
        const config = storedToken 
          ? { headers: { Authorization: `Bearer ${storedToken}` } }
          : { withCredentials: true };
        
        const response = await axios.get(`${API}/auth/me`, config);
        setUser(response.data);
        
        // Mark as backdoor if using token
        if (storedToken) {
          setIsBackdoor(true);
          setBackdoorToken(storedToken);
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        // Clear backdoor session on error
        sessionStorage.removeItem('backdoor_token');
        navigate('/admin');
      }
    };
    
    fetchUserData();
    
    // Setup WebSocket connection
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const newSocket = io(BACKEND_URL, {
      path: '/socket.io',
      // WebSocket first; polling only as fallback. Sticky nginx + Redis keep both safe.
      transports: ['websocket', 'polling'],
      upgrade: true,
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 20,
      reconnectionDelay: 1000,
      timeout: 20000,
    });
    
    newSocket.on('connect', () => {
      console.log('WebSocket connected, ID:', newSocket.id);
      newSocket.emit('subscribe_dashboard', {});
    });
    
    newSocket.on('connect_error', (error) => {
      console.log('WebSocket connection error:', error.message);
    });
    
    newSocket.on('attendance_update', (data) => {
      console.log('Attendance update received:', data);
      window.dispatchEvent(new CustomEvent('attendance_update', { detail: data }));
    });
    
    setSocket(newSocket);
    
    return () => {
      if (newSocket) newSocket.disconnect();
    };
  }, [navigate]);
  
  const handleLogout = async () => {
    try {
      // Use backdoor token if available
      const storedToken = sessionStorage.getItem('backdoor_token');
      const config = storedToken 
        ? { headers: { Authorization: `Bearer ${storedToken}` } }
        : { withCredentials: true };
      
      await axios.post(`${API}/auth/logout`, {}, config);
      
      // Clear backdoor session
      sessionStorage.removeItem('backdoor_token');
      delete axios.defaults.headers.common['Authorization'];
      
      // Close tab if backdoor, otherwise navigate to login
      if (isBackdoor) {
        window.close();
      } else {
        navigate('/admin');
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };
  
  const handleExitBackdoor = () => {
    // Clear backdoor session and close tab
    sessionStorage.removeItem('backdoor_token');
    delete axios.defaults.headers.common['Authorization'];
    window.close();
  };
  
  // Fetch pending leave requests count
  const fetchPendingLeaveCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/leave-requests/pending-count`, {
        withCredentials: true
      });
      setPendingLeaveCount(response.data.pending_count || 0);
    } catch (error) {
      console.error('Failed to fetch pending leave count:', error);
    }
  };
  
  // Fetch pending leave count on mount and periodically
  useEffect(() => {
    fetchPendingLeaveCount();
    const interval = setInterval(fetchPendingLeaveCount, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);
  
  // Main navigation items (without reports submenu items)
  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/admin/staff', icon: UserCog, label: t('nav.staff') },
    { path: '/admin/teams', icon: UsersRound, label: t('nav.teams') },
    { path: '/admin/attendance', icon: ClipboardCheck, label: t('nav.attendance') },
    { path: '/admin/leave-management', icon: CalendarOff, label: t('nav.leave_management'), badge: pendingLeaveCount },
    { path: '/admin/notifications', icon: Bell, label: t('nav.notifications') },
  ];
  
  // Reports submenu items - kept for reference, no longer used in sidebar
  // const reportsSubItems = [{ path: '/admin/reports', ... }];
  
  // Bottom navigation items
  const bottomNavItems = [
    { path: '/admin/payments', icon: CreditCard, label: t('nav.payments') },
    { path: '/admin/settings', icon: Settings, label: t('nav.settings') },
  ];
  
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[hsl(222_47%_7%)]">
      {/* Soft atmosphere — light only; dark uses solid shell */}
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.06),_transparent_45%)] dark:bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.08),_transparent_50%)]" />

      {/* Backdoor Warning Banner */}
      {isBackdoor && (
        <div className="fixed top-0 left-0 right-0 z-50 flex h-10 items-center justify-center gap-4 bg-amber-400 px-4 text-center text-sm font-semibold text-amber-950 shadow-sm dark:bg-amber-500/90 dark:text-amber-950">
          <span>Backdoor Mode — logged in as Super Admin</span>
          <Button
            size="sm"
            variant="outline"
            onClick={handleExitBackdoor}
            className="h-7 rounded-full border-amber-700/20 bg-white/90 px-3 text-xs font-semibold text-amber-950 hover:bg-white dark:border-amber-900/30 dark:bg-amber-50 dark:hover:bg-white"
          >
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
            Exit to Super Admin
          </Button>
        </div>
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed bottom-0 left-0 z-40 border-r border-slate-200/80 bg-white/95 shadow-[4px_0_24px_-12px_rgba(15,23,42,0.12)] backdrop-blur-xl
        dark:border-slate-800 dark:bg-[hsl(222_47%_9%)] dark:shadow-[4px_0_24px_-12px_rgba(0,0,0,0.45)]
        transition-all duration-300 ease-in-out
        ${sidebarOpen ? 'w-64' : 'w-[4.5rem]'}
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${isBackdoor ? 'top-10' : 'top-0'}
      `}
      data-testid="admin-sidebar"
      >
        <div className="flex h-full min-h-0 flex-col">
          {/* Brand */}
          <div className={`shrink-0 border-b border-slate-100 dark:border-slate-800 ${sidebarOpen ? 'px-5 py-5' : 'px-3 py-4'}`}>
            <div className={`flex items-center ${sidebarOpen ? 'justify-between gap-2' : 'flex-col gap-3'}`}>
              <div className={`flex min-w-0 items-center ${sidebarOpen ? 'gap-2.5' : 'justify-center'}`}>
                {branding.logo_url ? (
                  <img
                    src={branding.logo_url}
                    alt={branding.platform_name}
                    className={`object-contain transition-all ${sidebarOpen ? 'h-8' : 'h-7'}`}
                  />
                ) : (
                  <>
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-600/20">
                      <Building2 className="h-4 w-4" />
                    </div>
                    {sidebarOpen && (
                      <div className="min-w-0">
                        <h1 className="truncate text-base font-bold tracking-tight text-slate-900 dark:text-slate-50">
                          {branding.platform_name}
                        </h1>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-purple-600 dark:text-purple-400">
                          Admin
                        </p>
                      </div>
                    )}
                  </>
                )}
                {branding.logo_url && sidebarOpen && (
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-slate-900 dark:text-slate-50">{branding.platform_name}</p>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-purple-600 dark:text-purple-400">Admin</p>
                  </div>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen((v) => !v)}
                className="hidden h-8 w-8 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-200 lg:inline-flex"
                data-testid="sidebar-collapse-toggle"
                title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              >
                {sidebarOpen ? <ChevronsLeft className="h-4 w-4" /> : <ChevronsRight className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          
          {/* Navigation */}
          <nav className="min-h-0 flex-1 space-y-5 overflow-y-auto px-3 py-4" data-testid="admin-navigation">
            <div>
              {sidebarOpen && (
                <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
                  Workspace
                </p>
              )}
              <div className="space-y-1">
                {navItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) => `
                      group relative flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium transition-all
                      ${sidebarOpen ? '' : 'justify-center px-2'}
                      ${isActive
                        ? 'bg-gradient-to-r from-indigo-50 to-purple-50/80 text-indigo-700 shadow-sm ring-1 ring-indigo-100 dark:from-indigo-950/60 dark:to-purple-950/40 dark:text-indigo-300 dark:ring-indigo-800/60'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/70 dark:hover:text-slate-100'}
                    `}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-gradient-to-b from-indigo-500 to-purple-500" />
                        )}
                        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition ${
                          isActive
                            ? 'bg-white text-indigo-600 shadow-sm dark:bg-slate-900 dark:text-indigo-400'
                            : 'bg-slate-100/80 text-slate-500 group-hover:bg-white group-hover:text-slate-700 dark:bg-slate-800/80 dark:text-slate-400 dark:group-hover:bg-slate-800 dark:group-hover:text-slate-200'
                        }`}>
                          <item.icon className="h-4 w-4" />
                        </span>
                        {sidebarOpen && <span className="flex-1 truncate">{item.label}</span>}
                        {item.badge > 0 && (
                          <span className={`
                            flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white
                            ${sidebarOpen ? '' : 'absolute -right-0.5 -top-0.5'}
                          `}>
                            {item.badge > 99 ? '99+' : item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>

            <div>
              {sidebarOpen && (
                <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
                  Insights
                </p>
              )}
              <NavLink
                to="/admin/reports"
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) => `
                  group relative flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium transition-all
                  ${sidebarOpen ? '' : 'justify-center px-2'}
                  ${isActive || location.pathname.startsWith('/admin/reports')
                    ? 'bg-gradient-to-r from-indigo-50 to-purple-50/80 text-indigo-700 shadow-sm ring-1 ring-indigo-100 dark:from-indigo-950/60 dark:to-purple-950/40 dark:text-indigo-300 dark:ring-indigo-800/60'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/70 dark:hover:text-slate-100'}
                `}
                data-testid="nav-reports-menu"
              >
                {(location.pathname.startsWith('/admin/reports')) && (
                  <span className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-gradient-to-b from-indigo-500 to-purple-500" />
                )}
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition ${
                  location.pathname.startsWith('/admin/reports')
                    ? 'bg-white text-indigo-600 shadow-sm dark:bg-slate-900 dark:text-indigo-400'
                    : 'bg-slate-100/80 text-slate-500 group-hover:bg-white group-hover:text-slate-700 dark:bg-slate-800/80 dark:text-slate-400 dark:group-hover:bg-slate-800 dark:group-hover:text-slate-200'
                }`}>
                  <BarChart3 className="h-4 w-4" />
                </span>
                {sidebarOpen && <span className="flex-1 text-left">{t('nav.reports')}</span>}
              </NavLink>
            </div>

            <div>
              {sidebarOpen && (
                <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
                  Account
                </p>
              )}
              <div className="space-y-1">
                {bottomNavItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) => `
                      group relative flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium transition-all
                      ${sidebarOpen ? '' : 'justify-center px-2'}
                      ${isActive
                        ? 'bg-gradient-to-r from-indigo-50 to-purple-50/80 text-indigo-700 shadow-sm ring-1 ring-indigo-100 dark:from-indigo-950/60 dark:to-purple-950/40 dark:text-indigo-300 dark:ring-indigo-800/60'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/70 dark:hover:text-slate-100'}
                    `}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-gradient-to-b from-indigo-500 to-purple-500" />
                        )}
                        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition ${
                          isActive
                            ? 'bg-white text-indigo-600 shadow-sm dark:bg-slate-900 dark:text-indigo-400'
                            : 'bg-slate-100/80 text-slate-500 group-hover:bg-white group-hover:text-slate-700 dark:bg-slate-800/80 dark:text-slate-400 dark:group-hover:bg-slate-800 dark:group-hover:text-slate-200'
                        }`}>
                          <item.icon className="h-4 w-4" />
                        </span>
                        {sidebarOpen && <span className="truncate">{item.label}</span>}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          </nav>
          
          {/* User Menu */}
          <div className={`shrink-0 border-t border-slate-100 bg-gradient-to-t from-slate-50/90 to-white dark:border-slate-800 dark:from-slate-950 dark:to-[hsl(222_47%_9%)] ${sidebarOpen ? 'p-3' : 'p-2'}`}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={`h-auto w-full rounded-2xl hover:bg-slate-100 dark:hover:bg-slate-800 ${
                    sidebarOpen ? 'justify-start gap-3 px-2.5 py-2.5' : 'justify-center px-2 py-2'
                  }`}
                  data-testid="admin-user-menu-trigger"
                >
                  <Avatar className="h-9 w-9 ring-2 ring-white shadow-sm dark:ring-slate-700">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-600 text-sm font-bold text-white">
                      {user?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  {sidebarOpen && (
                    <div className="min-w-0 flex-1 text-left">
                      <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{user?.name}</p>
                      <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">{user?.email}</p>
                    </div>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="top" className="mb-1 w-56 rounded-2xl border-slate-200 p-1.5 shadow-xl dark:border-slate-700 dark:bg-[hsl(222_47%_9%)]">
                <DropdownMenuLabel className="px-2.5 py-2 text-xs text-slate-500">
                  {t('common.details')}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="dark:bg-slate-700" />
                <DropdownMenuItem
                  onClick={handleLogout}
                  data-testid="admin-logout-button"
                  className="rounded-xl text-rose-600 focus:bg-rose-50 focus:text-rose-700 dark:text-rose-400 dark:focus:bg-rose-950/40 dark:focus:text-rose-300"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>
      
      {/* Top Header Bar */}
      <header className={`
        fixed right-0 z-30 h-16 border-b border-slate-200/80 bg-white/95 shadow-[0_8px_24px_-18px_rgba(15,23,42,0.35)] backdrop-blur-xl
        dark:border-slate-800 dark:bg-[hsl(222_47%_9%/0.96)] dark:shadow-[0_8px_24px_-18px_rgba(0,0,0,0.5)]
        transition-all duration-300
        ${sidebarOpen ? 'lg:left-64' : 'lg:left-[4.5rem]'}
        left-0
        ${isBackdoor ? 'top-10' : 'top-0'}
      `}>
        <div className="flex h-full items-center justify-between gap-3 px-4 sm:px-5">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="h-10 w-10 rounded-2xl text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 lg:hidden"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <div className="hidden min-w-0 lg:block">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
                Company admin
              </p>
              <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                {t('nav.welcome')}, {user?.name}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div className="flex items-center gap-1 rounded-2xl bg-slate-100/80 p-1 ring-1 ring-slate-200/70 dark:bg-slate-800/80 dark:ring-slate-700">
              <ThemeToggle />
              <LanguageSelector compact />
            </div>
            <div className="rounded-2xl bg-slate-100/80 p-1 ring-1 ring-slate-200/70 dark:bg-slate-800/80 dark:ring-slate-700">
              <AlertsNotification />
            </div>
            <div className="hidden items-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-50 to-purple-50 px-3 py-1.5 ring-1 ring-indigo-100 dark:from-indigo-950/50 dark:to-purple-950/40 dark:ring-indigo-800/50 sm:flex">
              <Avatar className="h-7 w-7">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-indigo-600 text-[10px] font-bold text-white">
                  {user?.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <span className="max-w-[9rem] truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{user?.name}</span>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className={`
        transition-all duration-300
        ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-[4.5rem]'}
        ${isBackdoor ? 'mt-[104px]' : 'mt-16'}
        p-4 sm:p-6
      `}>
        <Routes>
          <Route path="dashboard" element={<AdminDashboardHome socket={socket} />} />
          <Route path="staff" element={<StaffPage />} />
          <Route path="staff/gps/:staffId" element={<StaffGPSSettingsPage />} />
          <Route path="teams" element={<TeamsPage />} />
          <Route path="attendance" element={<AttendancePage socket={socket} />} />
          <Route path="attendance/detail/:staffId" element={<StaffAttendanceDetailPage />} />
          <Route path="attendance/sessions/:staffId/:date" element={<AdminStaffSessionsPage />} />
          <Route
            path="attendance/idle/:staffId"
            element={<StaffAttendanceDetailPage />}
          />
          <Route
            path="attendance/breaks/:staffId"
            element={<StaffAttendanceDetailPage />}
          />
          <Route
            path="attendance/work/:staffId"
            element={<StaffAttendanceDetailPage />}
          />
          <Route path="leave-management" element={<AdminLeaveManagementPage />} />
          <Route path="notifications" element={<AdminNotificationsPage />} />
          <Route path="notifications/:notificationId/recipients" element={<NotificationRecipientsPage />} />
          <Route path="activities" element={<ActivitiesPage />} />
          <Route path="activities/screenshots/:staffId" element={<StaffScreenshotsPage />} />
          <Route path="activities/gps/:staffId" element={<StaffGPSTrackingPage />} />
          <Route path="usage-dashboard" element={<UsageDashboardPage />} />

          <Route path="usage-policy" element={<UsagePolicyPage />} />
          <Route path="usage-policy/staff/:staffId" element={<StaffUsageDetailPage />} />
          <Route path="usage-policy/staff/:staffId/app/:appName" element={<AppUrlDetailsPage />} />
          <Route path="reports" element={<ReportsHubPage />} />
          <Route path="reports/screenshots" element={<ScreenshotsReportPage />} />
          <Route path="reports/gps-tracking" element={<GPSTrackingReportPage />} />
          <Route path="reports/staff-activities" element={<StaffActivitiesPage />} />
          <Route path="reports/daily" element={<DailyReportPage />} />
          <Route path="reports/monthly" element={<MonthlyReportPage />} />
          <Route path="reports/usage-report" element={<UsageReportPage />} />
          <Route path="reports/usage-timeline" element={<UsageTimelinePage />} />
          <Route path="reports/apps-summary" element={<AppsSummaryPage />} />
          <Route path="reports/payroll/timesheet" element={<PayrollTimesheetPage />} />
          <Route path="reports/payroll/salary" element={<PayrollSalaryPage />} />
          <Route path="reports/payroll/overtime" element={<PayrollOvertimePage />} />
          <Route path="reports/payroll/leave" element={<PayrollLeavePage />} />
          <Route path="reports/payroll/audit" element={<PayrollAuditPage />} />
          <Route path="reports/sessions/:staffId/:date" element={<AdminStaffSessionsPage />} />
          <Route path="payroll-reports" element={<PayrollReportsPage />} />
          <Route path="activity-reports" element={<ActivityReportsPage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="settings" element={<AdminSettingsPage />} />
          <Route path="*" element={<AdminDashboardHome socket={socket} />} />
        </Routes>
      </main>
      
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/40 backdrop-blur-[2px] lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      
      {/* PWA Install Prompt for Mobile */}
      <PWAInstallPrompt />
    </div>
  );
}

export default CompanyAdminDashboard;
